import { useEffect, useRef, useState } from 'react';
import appIcon from '../assets/brand/mgl-icon.svg';

/* A working sandbox of LinksOS with hardcoded sample data. No auth, no
   network. Three tabs: a bookable tee sheet, a draggable pricing band,
   and a Flash Deal composer that pushes into the phone frame. */

const TIMES = [
  { t: '7:00a', d: 0.55 },
  { t: '7:50a', d: 0.75 },
  { t: '8:40a', d: 0.95 },
  { t: '9:30a', d: 1.0 },
  { t: '10:20a', d: 0.85 },
  { t: '11:10a', d: 0.66 },
  { t: '12:00p', d: 0.5 },
  { t: '12:50p', d: 0.42 },
  { t: '1:40p', d: 0.34 },
  { t: '2:30p', d: 0.38 },
  { t: '3:20p', d: 0.48 },
  { t: '4:10p', d: 0.4 },
];
const INIT_BOOKED = [2, 3, 4, 4, 3, 2, 1, 1, 0, 0, 1, 2];
const DEAL_WINDOW = [7, 8, 9];
const WINDOW_LABEL = '12:50p to 2:30p';
const CLAIMERS = ['A golfer', 'A second golfer'];
const BAND_MIN = 25;
const BAND_MAX = 95;
const DEFAULT_FLOOR = 42;
const DEFAULT_CEIL = 68;

const priceAt = (d, floor, ceil) => Math.round(floor + d * (ceil - floor));

function initialRows() {
  return TIMES.map((row, i) => {
    const seats = [null, null, null, null];
    for (let s = 0; s < INIT_BOOKED[i]; s += 1) {
      seats[s] = { src: s % 2 === 0 ? 'app' : 'walk', p: priceAt(row.d, DEFAULT_FLOOR, DEFAULT_CEIL) };
    }
    return seats;
  });
}

const TABS = [
  { key: 'sheet', label: 'Tee sheet' },
  { key: 'price', label: 'Dynamic pricing' },
  { key: 'deal', label: 'Flash Deals' },
];

export default function DemoPanel() {
  const [tab, setTab] = useState('sheet');
  const [rows, setRows] = useState(initialRows);
  const [floor, setFloor] = useState(DEFAULT_FLOOR);
  const [ceil, setCeil] = useState(DEFAULT_CEIL);
  const [pct, setPct] = useState(35);
  const [hours, setHours] = useState(2);
  const [deal, setDeal] = useState(null);
  const [claims, setClaims] = useState([]);
  const timers = useRef([]);
  const tabRefs = useRef({});
  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const basePrice = (i) => priceAt(TIMES[i].d, floor, ceil);
  const rowDealed = (i) => deal !== null && DEAL_WINDOW.includes(i);
  const shownPrice = (i) =>
    rowDealed(i) ? Math.round(basePrice(i) * (1 - deal.pct / 100)) : basePrice(i);

  const booked = rows.flat().filter(Boolean);
  const revenue = booked.reduce((sum, s) => sum + s.p, 0);
  const fill = Math.round((booked.length / 48) * 100);
  const openInWindow = DEAL_WINDOW.reduce(
    (n, i) => n + rows[i].filter((s) => s === null).length,
    0
  );
  const previewBase = basePrice(8);
  const previewDeal = Math.round(previewBase * (1 - pct / 100));
  const liveDealPrice = deal ? Math.round(previewBase * (1 - deal.pct / 100)) : previewDeal;

  const bookSeat = (ri, si) => {
    setRows((prev) => {
      if (prev[ri][si] !== null) return prev;
      const next = prev.map((r) => r.slice());
      next[ri][si] = rowDealed(ri)
        ? { src: 'deal', p: shownPrice(ri) }
        : { src: 'app', p: basePrice(ri) };
      return next;
    });
  };

  const claimSeatInWindow = (name, dealPct) => {
    const current = rowsRef.current;
    for (const i of DEAL_WINDOW) {
      const si = current[i].indexOf(null);
      if (si !== -1) {
        const p = Math.round(priceAt(TIMES[i].d, floor, ceil) * (1 - dealPct / 100));
        setRows((prev) => {
          if (prev[i][si] !== null) return prev;
          const next = prev.map((r) => r.slice());
          next[i][si] = { src: 'deal', p };
          return next;
        });
        setClaims((c) => [...c, { name, time: TIMES[i].t, p }]);
        return;
      }
    }
  };

  const pushDeal = () => {
    if (deal || openInWindow === 0) return;
    const thisDeal = { pct, hours };
    setDeal(thisDeal);
    setClaims([]);
    timers.current.push(
      setTimeout(() => claimSeatInWindow(CLAIMERS[0], thisDeal.pct), 1400),
      setTimeout(() => claimSeatInWindow(CLAIMERS[1], thisDeal.pct), 3300)
    );
  };

  const endDeal = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setDeal(null);
    setClaims([]);
  };

  const resetAll = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setRows(initialRows());
    setFloor(DEFAULT_FLOOR);
    setCeil(DEFAULT_CEIL);
    setPct(35);
    setHours(2);
    setDeal(null);
    setClaims([]);
  };

  const onTabKey = (e) => {
    const idx = TABS.findIndex((t) => t.key === tab);
    let next = null;
    if (e.key === 'ArrowRight') next = TABS[(idx + 1) % TABS.length].key;
    if (e.key === 'ArrowLeft') next = TABS[(idx + TABS.length - 1) % TABS.length].key;
    if (next) {
      e.preventDefault();
      setTab(next);
      const el = tabRefs.current[next];
      if (el) el.focus();
    }
  };

  const span = (BAND_MAX - BAND_MIN) / 100;
  const bandLeft = (floor - BAND_MIN) / span;
  const bandWidth = (ceil - floor) / span;

  const srcLabel = { app: 'booked from the app', walk: 'walk-in', deal: 'Flash Deal booking' };

  return (
    <div className="demo-grid">
      <div className="dp on-pine">
        <div className="dp-bar">
          <span className="dp-dot" /><span className="dp-dot" /><span className="dp-dot" />
          <span className="dp-title">LinksOS · Fair Oaks GC · sample data</span>
        </div>

        <div className="dp-tabs" role="tablist" aria-label="LinksOS demo sections" onKeyDown={onTabKey}>
          {TABS.map((t) => (
            <button
              key={t.key}
              ref={(el) => { tabRefs.current[t.key] = el; }}
              type="button"
              role="tab"
              id={`dp-tab-${t.key}`}
              aria-selected={tab === t.key}
              aria-controls={`dp-panel-${t.key}`}
              tabIndex={tab === t.key ? 0 : -1}
              className="dp-tab"
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="dp-body">
          {tab === 'sheet' && (
            <div role="tabpanel" id="dp-panel-sheet" aria-labelledby="dp-tab-sheet">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 6px 10px' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--cream-mut)' }}>
                  Saturday. Click any open square to book it.
                </span>
              </div>
              {TIMES.map((row, ri) => (
                <div className="dp-row" key={row.t}>
                  <span className="dp-time">{row.t}</span>
                  <span className={`dp-price${rowDealed(ri) ? ' dealed' : ''}`}>${shownPrice(ri)}</span>
                  <span className="dp-seats">
                    {rows[ri].map((seat, si) => (
                      <button
                        key={si}
                        type="button"
                        className={`dp-seat${seat ? ` ${seat.src}` : ''}`}
                        disabled={seat !== null}
                        aria-label={
                          seat
                            ? `${row.t} seat ${si + 1}, ${srcLabel[seat.src]} at $${seat.p}`
                            : `Book the open ${row.t} seat for $${shownPrice(ri)}`
                        }
                        onClick={() => bookSeat(ri, si)}
                      />
                    ))}
                  </span>
                  {rowDealed(ri) ? <span className="dp-chip">deal</span> : <span />}
                </div>
              ))}
              <div className="dp-legend" aria-hidden="true">
                <span className="dp-leg"><i style={{ background: '#4e8b68' }} />App</span>
                <span className="dp-leg"><i style={{ background: 'rgba(243,240,228,0.55)' }} />Walk-in</span>
                <span className="dp-leg"><i style={{ background: 'var(--flag)' }} />Flash Deal</span>
                <span className="dp-leg"><i style={{ border: '1.5px solid rgba(243,240,228,0.4)' }} />Open</span>
              </div>
            </div>
          )}

          {tab === 'price' && (
            <div role="tabpanel" id="dp-panel-price" aria-labelledby="dp-tab-price">
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 14, alignItems: 'center', padding: '2px 6px 6px' }}>
                <span className="mono" style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--gold-bright)' }}>${floor}</span>
                <div className="bandbox">
                  <div className="band-rail" />
                  <div className="band-fill" style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }} />
                  <input
                    type="range"
                    className="band-input"
                    min={BAND_MIN}
                    max={BAND_MAX}
                    value={floor}
                    aria-label="Price floor"
                    aria-valuetext={`Floor $${floor}`}
                    onChange={(e) => setFloor(Math.min(Number(e.target.value), ceil - 6))}
                  />
                  <input
                    type="range"
                    className="band-input"
                    min={BAND_MIN}
                    max={BAND_MAX}
                    value={ceil}
                    aria-label="Price ceiling"
                    aria-valuetext={`Ceiling $${ceil}`}
                    onChange={(e) => setCeil(Math.max(Number(e.target.value), floor + 6))}
                  />
                </div>
                <span className="mono" style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--gold-bright)' }}>${ceil}</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--cream-faint)', padding: '0 6px 12px' }}>
                Drag the handles. The engine reprices the day inside your band and never steps outside it.
              </p>
              {TIMES.map((row, i) => (
                <div className="dp-prow" key={row.t}>
                  <span className="dp-time">{row.t}</span>
                  <div className="dp-ptrack" aria-hidden="true">
                    <span className="dp-pdot" style={{ left: `${(basePrice(i) - BAND_MIN) / span}%` }} />
                  </div>
                  <span className="dp-price" style={{ textAlign: 'right' }}>${basePrice(i)}</span>
                </div>
              ))}
              <p style={{ fontSize: '0.78rem', color: 'var(--cream-faint)', padding: '10px 6px 0' }}>
                Demand here is sample data: weekend morning peak, midday dip. Booked seats keep the
                price they were sold at. Only open times move.
              </p>
            </div>
          )}

          {tab === 'deal' && (
            <div role="tabpanel" id="dp-panel-deal" aria-labelledby="dp-tab-deal">
              <p style={{ fontSize: '0.92rem', color: 'var(--cream-mut)', padding: '2px 6px 14px' }}>
                {openInWindow > 0
                  ? `${openInWindow} open seats from ${WINDOW_LABEL}. Move them without touching your morning rate.`
                  : `The ${WINDOW_LABEL} window is full. End the deal or reset the sandbox to run it again.`}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 6px 10px' }} role="group" aria-label="Discount">
                {[25, 35, 45].map((v) => (
                  <button key={v} type="button" className="dp-chipbtn" aria-pressed={pct === v} onClick={() => setPct(v)}>
                    {v}% off
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 6px 16px' }} role="group" aria-label="Deal window">
                {[2, 3].map((v) => (
                  <button key={v} type="button" className="dp-chipbtn" aria-pressed={hours === v} onClick={() => setHours(v)}>
                    {v} hour window
                  </button>
                ))}
              </div>
              <div
                style={{
                  border: '1px solid rgba(243,240,228,0.16)', borderRadius: 10,
                  padding: '12px 14px', margin: '0 6px 16px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: '0.85rem', color: 'var(--cream-mut)' }}>
                  {pct}% off, {WINDOW_LABEL}
                </span>
                <span className="mono" style={{ fontSize: '1rem' }}>
                  <s style={{ color: 'var(--cream-faint)' }}>${previewBase}</s>{' '}
                  <strong style={{ color: 'var(--gold-bright)' }}>${previewDeal}</strong>
                </span>
              </div>
              {deal === null ? (
                <div style={{ padding: '0 6px' }}>
                  <button type="button" className="dp-push" disabled={openInWindow === 0} onClick={pushDeal}>
                    Push the deal to nearby golfers
                  </button>
                </div>
              ) : (
                <div style={{ padding: '0 6px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="dp-chip">Live · {deal.pct}% off · {deal.hours}h</span>
                  <span style={{ fontSize: '0.82rem', color: 'var(--cream-mut)' }}>
                    Watch the phone. Claims land on your sheet.
                  </span>
                  <button type="button" className="dp-chipbtn" onClick={endDeal}>End the deal</button>
                </div>
              )}
              <p style={{ fontSize: '0.78rem', color: 'var(--cream-faint)', padding: '14px 6px 0' }}>
                Flash Deals only run when you fire one, and they end when the window closes or you
                end them. Nothing here is automated.
              </p>
            </div>
          )}
        </div>

        <div className="dp-kpis">
          <div>
            <div className="dp-kpi-v">{fill}%</div>
            <div className="dp-kpi-l">Sheet filled</div>
          </div>
          <div>
            <div className="dp-kpi-v">${revenue.toLocaleString('en-US')}</div>
            <div className="dp-kpi-l">Booked revenue</div>
          </div>
          <div>
            <div className="dp-kpi-v">{48 - booked.length}</div>
            <div className="dp-kpi-l">Open seats</div>
          </div>
          <button type="button" className="dp-reset" onClick={resetAll} style={{ marginLeft: 'auto' }}>
            Reset the sandbox
          </button>
        </div>
      </div>

      <div className="ph" aria-label="What golfers see in the MyGolfLinks app">
        <div className="ph-notch" />
        <div className="ph-screen">
          <div className="ph-app">
            <img src={appIcon} alt="" width="15" height="15" style={{ borderRadius: 4, display: 'block' }} />
            MyGolfLinks
          </div>
          <div className="mono" style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cream-faint)', padding: '0 2px 10px' }}>
            Deals near you
          </div>
          {deal === null ? (
            <p className="ph-empty">
              Nothing right now. When a course near you fires a Flash Deal, it shows up here.
            </p>
          ) : (
            <>
              <div className="ph-note hl">
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--cream)' }}>
                  Flash Deal · Fair Oaks GC
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--cream-mut)' }}>
                  {deal.pct}% off, today {WINDOW_LABEL}
                </div>
              </div>
              <div className="ph-deal hl" style={{ animationDelay: '0.12s' }}>
                <div style={{ fontWeight: 800, fontSize: '0.85rem' }}>Fair Oaks GC</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--mut)', marginBottom: 6 }}>
                  Today, {WINDOW_LABEL} · 18 holes with cart
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="mono" style={{ fontSize: '1.25rem', fontWeight: 600 }}>${liveDealPrice}</span>
                  <s className="mono" style={{ fontSize: '0.8rem', color: 'var(--faint)' }}>${previewBase}</s>
                  <span className="mono" style={{ marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 600, color: 'var(--flag)' }}>
                    {deal.pct}% OFF
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 10, background: 'var(--pine)', color: 'var(--cream)',
                    borderRadius: 8, textAlign: 'center', padding: '8px 0',
                    fontSize: '0.78rem', fontWeight: 700,
                  }}
                >
                  Book a spot
                </div>
              </div>
              {claims.map((c) => (
                <div key={c.name} className="hl" style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '10px 4px 0', fontSize: '0.72rem', color: 'var(--cream-mut)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--flag)', flexShrink: 0, position: 'relative', top: -1 }} />
                  {c.name} booked {c.time} for ${c.p}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
